import { z } from 'zod';
import { ModuleManifest } from '@momentum/shared';

/**
 * DISPOSABLE fixture. Proves: exact four-player eligibility, two fixed teams, team-private payloads kept out of the
 * other team's and the spectator's projections, a shared deadline, `teams` voice scope, and typed team results.
 * Symbolic "compliance incident" elements only; not a training claim, not a finished game.
 */
export const FIXTURE_TEAM_2V2_ID = 'fixture-team-2v2';

export const fixtureTeam2v2Manifest = ModuleManifest.parse({
  id: FIXTURE_TEAM_2V2_ID,
  version: '0.1.0',
  name: 'Two-versus-two fixture (disposable)',
  description: 'Two teams each hold a private code fragment and must submit it before the deadline.',
  disposable: true,
  players: { min: 4, max: 4 },
  participation: 'teams',
  teams: { count: 2, minPerTeam: 2, maxPerTeam: 2 },
  voice: { scope: 'teams' },
  capabilities: { launchesChildren: false, childModuleIds: [], usesRandom: true, hasPrivateData: true, renderer: 'fixture-team-2v2' },
  launchableFromLobby: true,
});

export const FixtureTeam2v2Config = z.object({
  deadlineMs: z.number().int().min(5_000).max(300_000).default(60_000),
});
export type FixtureTeam2v2Config = z.infer<typeof FixtureTeam2v2Config>;

export const FixtureTeam2v2Action = z.discriminatedUnion('type', [
  /** Any member of a team may submit once for their team. */
  z.object({ type: z.literal('submit'), code: z.string().min(1).max(16) }),
]);
export type FixtureTeam2v2Action = z.infer<typeof FixtureTeam2v2Action>;

/** Projection: `myTeam` carries the team-private fragment only for members; other teams and spectators see status only. */
export const FixtureTeam2v2View = z.object({
  phase: z.enum(['active', 'complete']),
  teams: z.array(
    z.object({
      teamId: z.string(),
      label: z.string(),
      participantIds: z.array(z.string()),
      submitted: z.boolean(),
      solved: z.boolean().nullable(),
    }),
  ),
  myTeam: z
    .object({
      teamId: z.string(),
      /** Team-private. Must never appear in another team's or the spectator's frames. */
      fragment: z.string(),
      canSubmit: z.boolean(),
    })
    .nullable(),
});
export type FixtureTeam2v2View = z.infer<typeof FixtureTeam2v2View>;

export const FixtureTeam2v2ResultPayload = z.object({
  schema: z.literal(1),
  teams: z.array(z.object({ teamId: z.string(), solved: z.boolean(), submittedAt: z.number().nullable() })),
});
export type FixtureTeam2v2ResultPayload = z.infer<typeof FixtureTeam2v2ResultPayload>;
